import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useListing, useListings } from '../hooks/useListings';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../supabaseClient';
import ListingReportModal from '../components/ListingReportModal';
import PropertyMiniMap from '../components/PropertyMiniMap';
import SimilarListings from '../components/SimilarListings';
import MaskedPhone from '../components/MaskedPhone';
import Avatar from '../components/Avatar';
import { recordView } from '../lib/viewHistory';
import './PropertyDetailPage.css';

const IconBed = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4"/><path d="M2 20v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5"/><line x1="2" y1="13" x2="22" y2="13"/></svg>;
const IconBath = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12h16a1 1 0 0 1 1 1v3a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4v-3a1 1 0 0 1 1-1z"/><path d="M6 12V6a3 3 0 0 1 3-3h0a3 3 0 0 1 3 3v1"/></svg>;
const IconArea = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>;
const IconType = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
const IconEnergy = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
const IconPin = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const IconPhone = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6.29 6.29l.87-.87a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.79 17z"/></svg>;
const IconMail = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
const IconCheck = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const IconHeart = ({ filled }) => <svg width="17" height="17" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
const IconBack = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>;
const IconImage = () => <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
const IconChevLeft = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>;
const IconChevRight = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>;
const IconMsg = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;

// ── Feature icons ──
const _fi = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const IPool    = () => <svg {..._fi}><path d="M2 12c1.5-1.5 3-1.5 4.5 0s3 1.5 4.5 0 3-1.5 4.5 0 3 1.5 4.5 0"/><path d="M2 17c1.5-1.5 3-1.5 4.5 0s3 1.5 4.5 0 3-1.5 4.5 0 3 1.5 4.5 0"/><path d="M12 3v6"/><path d="M9 6h6"/></svg>;
const IWaves   = () => <svg {..._fi}><path d="M2 6c1.5-1.5 3-1.5 4.5 0s3 1.5 4.5 0 3-1.5 4.5 0 3 1.5 4.5 0"/><path d="M2 12c1.5-1.5 3-1.5 4.5 0s3 1.5 4.5 0 3-1.5 4.5 0 3 1.5 4.5 0"/><path d="M2 18c1.5-1.5 3-1.5 4.5 0s3 1.5 4.5 0 3-1.5 4.5 0 3 1.5 4.5 0"/></svg>;
const IEye     = () => <svg {..._fi}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const ILeaf    = () => <svg {..._fi}><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/></svg>;
const ISchool  = () => <svg {..._fi}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>;
const IUtensil = () => <svg {..._fi}><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/></svg>;
const IBell    = () => <svg {..._fi}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
const ICal     = () => <svg {..._fi}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 16 11 18 15 14"/></svg>;
const IWifi    = () => <svg {..._fi}><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>;
const IWine    = () => <svg {..._fi}><path d="M8 22h8"/><path d="M7 10h10"/><path d="M12 15v7"/><path d="M12 15a5 5 0 0 0 5-5c0-2-.5-4-2-8H7c-1.5 4-2 6-2 8a5 5 0 0 0 5 5z"/></svg>;
const IFilm    = () => <svg {..._fi}><rect x="2" y="2" width="20" height="20" rx="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="17" x2="22" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/></svg>;
const IMaximize= () => <svg {..._fi}><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>;
const ISun     = () => <svg {..._fi}><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/></svg>;
const IBus     = () => <svg {..._fi}><path d="M8 6v6"/><path d="M15 6v6"/><path d="M2 12h19.6"/><path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3"/><circle cx="7" cy="18" r="2"/><path d="M9 18h5"/><circle cx="16" cy="18" r="2"/></svg>;
const ISofa    = () => <svg {..._fi}><path d="M20 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3"/><path d="M2 16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0v1.5H6V11a2 2 0 0 0-4 0z"/><path d="M4 18v2"/><path d="M20 18v2"/><path d="M12 4v9"/></svg>;
const IZap     = () => <svg {..._fi}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
const ITag     = () => <svg {..._fi}><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>;
const ICard    = () => <svg {..._fi}><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>;
const IAnchor  = () => <svg {..._fi}><circle cx="12" cy="5" r="3"/><line x1="12" y1="22" x2="12" y2="8"/><path d="M5 12H2a10 10 0 0 0 20 0h-3"/></svg>;
const ICar     = () => <svg {..._fi}><path d="M5 17H3a2 2 0 0 1-2-2V9l3-6h14l3 6v6a2 2 0 0 1-2 2h-2"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>;
const IUmbrella= () => <svg {..._fi}><path d="M23 12a11.05 11.05 0 0 0-22 0zm-5 7a3 3 0 0 1-6 0v-7"/></svg>;
const IShield  = () => <svg {..._fi}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
const ISnowflake= () => <svg {..._fi}><line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/><path d="M20 16l-4-4 4-4"/><path d="M4 8l4 4-4 4"/><path d="M16 4l-4 4-4-4"/><path d="M8 20l4-4 4 4"/></svg>;
const IFlame   = () => <svg {..._fi}><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 3z"/></svg>;
const IStar    = () => <svg {..._fi}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
const IBuilding= () => <svg {..._fi}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M3 15h18"/><path d="M9 3v18"/><path d="M15 3v18"/></svg>;
const IPaw     = () => <svg {..._fi}><circle cx="11" cy="4" r="2"/><circle cx="18" cy="8" r="2"/><circle cx="20" cy="16" r="2"/><path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z"/></svg>;

function getFeatureIcon(feature) {
  const f = feature.toLowerCase();
  if (f.includes('pool'))                                    return <IPool />;
  if (f.includes('sea view') || f.includes('ocean view'))   return <IWaves />;
  if (f.includes('beachfront') || f.includes('coastal'))    return <IUmbrella />;
  if (f.includes('view'))                                    return <IEye />;
  if (f.includes('terrace') || f.includes('balcony'))       return <IBuilding />;
  if (f.includes('garden') || f.includes('landscap'))       return <ILeaf />;
  if (f.includes('school') || f.includes('universit'))      return <ISchool />;
  if (f.includes('kitchen') || f.includes('chef'))          return <IUtensil />;
  if (f.includes('concierge') || f.includes('reception'))   return <IBell />;
  if (f.includes('available'))                              return <ICal />;
  if (f.includes('smart') || f.includes('wifi') || f.includes('tech')) return <IWifi />;
  if (f.includes('wine') || f.includes('cellar'))           return <IWine />;
  if (f.includes('cinema') || f.includes('theatre') || f.includes('theater') || f.includes('movie')) return <IFilm />;
  if (f.includes('ceiling') || f.includes('spacious'))      return <IMaximize />;
  if (f.includes('natural light') || f.includes('bright') || f.includes('sunny')) return <ISun />;
  if (f.includes('bus') || f.includes('transit') || f.includes('transport')) return <IBus />;
  if (f.includes('furnished') || f.includes('furniture'))   return <ISofa />;
  if (f.includes('energy') || f.includes('solar') || f.includes('a+')) return <IZap />;
  if (f.includes('pre-launch') || f.includes('price') || f.includes('deal')) return <ITag />;
  if (f.includes('payment') || f.includes('flexible'))      return <ICard />;
  if (f.includes('marina') || f.includes('yacht') || f.includes('port')) return <IAnchor />;
  if (f.includes('parking') || f.includes('garage') || f.includes('car')) return <ICar />;
  if (f.includes('beach'))                                  return <IWaves />;
  if (f.includes('security') || f.includes('gated'))        return <IShield />;
  if (f.includes('air con') || f.includes('cooling') || f.includes('a/c')) return <ISnowflake />;
  if (f.includes('heat') || f.includes('warm') || f.includes('underfloor')) return <IFlame />;
  if (f.includes('pet') || f.includes('dog') || f.includes('cat')) return <IPaw />;
  return <IStar />;
}


function getGallerySlots(listing, t) {
  const rooms = [
    t('property.rooms.livingRoom'),
    t('property.rooms.masterBedroom'),
    t('property.rooms.kitchen'),
    t('property.rooms.bathroom'),
    t('property.rooms.gardenTerrace'),
    t('property.rooms.exterior'),
  ];
  if (listing.images && listing.images.length > 0) {
    return listing.images.map((src, i) => ({ src, label: rooms[i] || `${t('property.photoLabel')} ${i + 1}`, hasImage: true }));
  }
  return rooms.map((label) => ({ src: null, label, hasImage: false }));
}

export default function PropertyDetailPage() {
  const { t } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, addToWatchlist, removeFromWatchlist, isInWatchlist, isPro, hasProAccess } = useAuth();
  const { listing, loading, error: loadError } = useListing(parseInt(id));
  const { listings: allListings } = useListings();
  const [loadTimedOut, setLoadTimedOut] = useState(false);

  // If the listing hasn't resolved after 10s, surface a clear error state
  // rather than an indefinite spinner. Reset the flag whenever loading toggles.
  useEffect(() => {
    setLoadTimedOut(false);
    if (!loading) return;
    const timer = setTimeout(() => setLoadTimedOut(true), 10000);
    return () => clearTimeout(timer);
  }, [loading, id]);
  const [msgText, setMsgText] = useState(() => t('property.defaultMessage'));
  const [msgSent, setMsgSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [showListingReport, setShowListingReport] = useState(false);
  const [brokerAvatarUrl, setBrokerAvatarUrl] = useState(null);

  // Track listing view: 5s delay, skip owner, deduplicate per session.
  React.useEffect(() => {
    if (!listing?.id) return;
    const sessionKey = `viewed_listing_${listing.id}`;
    if (sessionStorage.getItem(sessionKey)) return;
    if (user?.id && listing.user_id === user.id) return;
    const timer = setTimeout(() => {
      supabase.rpc('increment_listing_views', { listing_id: listing.id });
      // Hybrid: writes localStorage immediately + UPSERTs into listing_views
      // when logged in. Fire-and-forget — promise rejection is swallowed
      // inside the wrapper.
      recordView(listing, user);
      sessionStorage.setItem(sessionKey, '1');
    }, 5000);
    return () => clearTimeout(timer);
  }, [listing?.id, listing?.user_id, user?.id]);

  // Fetch broker avatar by the listing's contact_email once the listing loads.
  React.useEffect(() => {
    if (!listing?.contact_email) return;
    supabase
      .from('brokers')
      .select('avatar_url')
      .eq('email', listing.contact_email)
      .maybeSingle()
      .then(({ data }) => { if (data?.avatar_url) setBrokerAvatarUrl(data.avatar_url); });
  }, [listing?.contact_email]);

  const saved = listing ? isInWatchlist(listing.id) : false;

  const handleSave = () => {
    if (!user) { navigate('/login', { state: { from: location.pathname } }); return; }
    if (saved) removeFromWatchlist(listing.id);
    else addToWatchlist(listing);
  };

  const handleSendMessage = async () => {
    if (!user) { navigate('/login', { state: { from: location.pathname } }); return; }
    if (!msgText.trim()) return;
    setSending(true);
    try {
      const { data: listingData } = await supabase
        .from('listings').select('user_id').eq('id', listing.id).single();
      const agentId = listingData?.user_id;
      if (!agentId || agentId === user.id) { setMsgSent(true); setSending(false); return; }

      const { data: existing } = await supabase
        .from('conversations').select('id')
        .eq('listing_id', listing.id).eq('buyer_id', user.id).eq('agent_id', agentId)
        .maybeSingle();

      let convoId = existing?.id;
      if (!convoId) {
        const { data: newConvo } = await supabase.from('conversations').insert({
          listing_id: listing.id, buyer_id: user.id, agent_id: agentId,
          last_message: msgText.trim(), last_message_at: new Date().toISOString(),
          agent_unread: 1, is_pro_sender: isPro || hasProAccess,
        }).select('id').single();
        convoId = newConvo?.id;
      }

      if (convoId) {
        await supabase.from('messages').insert({
          conversation_id: convoId, sender_id: user.id, content: msgText.trim(),
        });
        await supabase.from('conversations').update({
          last_message: msgText.trim(), last_message_at: new Date().toISOString(),
          agent_unread: 1, is_pro_sender: isPro || hasProAccess,
        }).eq('id', convoId);
      }
      setMsgSent(true);
      setTimeout(() => navigate('/notifications'), 1500);
    } catch (err) {
      console.error('Failed to send message:', err);
      setMsgSent(true);
    }
    setSending(false);
  };

  if (loading && !loadTimedOut && !loadError) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: 40, height: 40, border: '3px solid #EFEEE9', borderTopColor: '#0E0E10', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  if (loading && (loadTimedOut || loadError)) return (
    <div className="detail-load-error">
      <h2>This is taking longer than expected.</h2>
      <p>Something on our end is slow to respond. Please try again.</p>
      <div className="detail-load-error__actions">
        <button className="detail-load-error__btn detail-load-error__btn--primary" onClick={() => window.location.reload()}>
          Try again
        </button>
        <button className="detail-load-error__btn detail-load-error__btn--outline" onClick={() => navigate(-1)}>
          Go back
        </button>
      </div>
    </div>
  );

  if (!listing) return (
    <div className="detail-404">
      <h2>{t('property.notFound')}</h2>
      <button onClick={() => navigate('/listings')}>{t('property.backToListings')}</button>
    </div>
  );

  const formatPrice = (price, type) =>
    type === 'rent' ? `€${price.toLocaleString()}${t('property.perMonthShort')}` : `€${price.toLocaleString()}`;

  const gallery = getGallerySlots(listing, t);
  const contactLabel = listing.priceType === 'rent' ? t('property.messageLandlord') : t('property.sendMessage');

  return (
    <main className="detail">
      <div className="detail__gallery-wrap">
        <div className="detail__gallery-preview" style={{ background: listing.gradient }}>
          {gallery[activePhoto]?.hasImage ? (
            <>
              <img
                src={gallery[activePhoto].src}
                alt={gallery[activePhoto].label}
                loading="eager"
                fetchpriority="high"
                decoding="async"
                width="1200"
                height="800"
              />
              <button className="detail__gallery-prev" onClick={() => setActivePhoto(p => Math.max(0, p - 1))}><IconChevLeft /></button>
              <button className="detail__gallery-next" onClick={() => setActivePhoto(p => Math.min(gallery.length - 1, p + 1))}><IconChevRight /></button>
            </>
          ) : (
            <div className="detail__gallery-preview-empty">
              <IconImage />
              <div className="detail__gallery-preview-label">{gallery[activePhoto]?.label}</div>
              <p>{t('property.noPhoto')}</p>
            </div>
          )}
          <button className="detail__back" onClick={() => navigate(-1)}><IconBack /> {t('property.back')}</button>
          <button className={`detail__fav ${saved ? 'detail__fav--saved' : ''}`} onClick={handleSave} aria-label={saved ? t('property.saved') : t('property.save')}>
            <IconHeart filled={saved} />
          </button>
          <div className="detail__gallery-counter">{activePhoto + 1} / {gallery.length}</div>
        </div>

        <div className="detail__gallery">
          {gallery.map((photo, i) => (
            <div key={i} className={`detail__gallery-thumb ${activePhoto === i ? 'detail__gallery-thumb--active' : ''}`} style={{ background: listing.gradient }} onClick={() => setActivePhoto(i)}>
              {photo.hasImage ? (
                <img
                  src={photo.src}
                  alt={photo.label}
                  loading="lazy"
                  decoding="async"
                  width="200"
                  height="140"
                />
              ) : (
                <div className="detail__gallery-empty"><IconImage /><span>{photo.label}</span></div>
              )}
            </div>
          ))}
        </div>

        <div className="detail__gallery-sidebar">
          <div className="detail__agent-card" style={{ position: 'relative' }}>
            <button
              className="detail__agent-flag"
              onClick={() => setShowListingReport(true)}
              title="Report this listing"
              aria-label="Report this listing"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
                <line x1="4" y1="22" x2="4" y2="15"/>
              </svg>
            </button>
  <Avatar avatarUrl={brokerAvatarUrl} name={listing.agent.name} size={46} />
  <div className="detail__agent-name">{listing.agent.name}</div>
  <div className="detail__agent-agency">
    {listing.agency_name ? `Listed with ${listing.agency_name}` : listing.agent.agency}
  </div>
  <MaskedPhone phone={listing.contact_phone} icon={<IconPhone />} className="detail__agent-phone" />
  {listing.agency_name && listing.contact_email && (
    <a href={`mailto:${listing.contact_email}`} className="detail__agent-email">
      <IconMail /> {listing.contact_email}
    </a>
  )}
            <div className="detail__price-card">
              <div className="detail__price-big">{formatPrice(listing.price, listing.priceType)}</div>
              <div className="detail__price-type">{listing.priceType === 'rent' ? t('property.perMonth') : t('property.askingPrice')}</div>
            </div>
            {msgSent ? (
              <div className="detail__msg-success">
                <IconCheck /> {t('property.messageSent')}
              </div>
            ) : (
              <>
                <textarea
                  className="detail__textarea"
                  placeholder={t('property.defaultMessage')}
                  rows={4}
                  value={msgText}
                  onChange={e => setMsgText(e.target.value)}
                />
                <button className="detail__contact-btn" onClick={handleSendMessage} disabled={sending}>
                  <IconMsg /> {sending ? t('property.sending') : contactLabel}
                </button>
              </>
            )}
          </div>

        </div>
      </div>

      <div className="detail__body">
        {listing.status === 'sold' && <span className="detail__soldTag">SOLD</span>}

        {/* Header — matches PropertyCard grammar: gold kicker → title → loc → price */}
        <div className="detail__kicker">
          {(listing.priceType === 'rent' ? t('card.forRent') : t('card.forSale'))}
          {listing.type ? ` · ${listing.type.charAt(0).toUpperCase() + listing.type.slice(1).toLowerCase()}` : ''}
        </div>
        <h1 className="detail__title">{listing.title}</h1>
        <p className="detail__loc"><IconPin /> {listing.city}, {listing.region}</p>
        <div className="detail__price">{formatPrice(listing.price, listing.priceType)}</div>

        <div className={`detail__specs ${listing.epc_rating ? 'detail__specs--with-energy' : ''}`}>
          <div className="detail__spec"><div className="detail__spec-icon"><IconBed /></div><strong>{listing.bedrooms}</strong><label>{t('property.bedrooms')}</label></div>
          <div className="detail__spec"><div className="detail__spec-icon"><IconBath /></div><strong>{listing.bathrooms}</strong><label>{t('property.bathrooms')}</label></div>
          <div className="detail__spec"><div className="detail__spec-icon"><IconArea /></div><strong>{listing.size} m²</strong><label>{t('property.floorArea')}</label></div>
          <div className="detail__spec"><div className="detail__spec-icon"><IconType /></div><strong>{listing.type}</strong><label>{t('property.type')}</label></div>
          {listing.epc_rating && (
            <div className="detail__spec"><div className="detail__spec-icon"><IconEnergy /></div><strong>{listing.epc_rating}</strong><label>Energy Rating</label></div>
          )}
        </div>

        <div className="detail__split">
          <div className="detail__section">
            <h3>{t('property.description')}</h3>
            <p>{listing.description}</p>
          </div>

          <div className="detail__section">
            <h3>{t('property.features')}</h3>
            <div className="detail__features">
              {listing.features.map(f => (
                <span key={f} className="detail__feature">{getFeatureIcon(f)} {f}</span>
              ))}
            </div>
          </div>
        </div>

        {listing.show_map !== false && <PropertyMiniMap listing={listing} />}

        <SimilarListings listing={listing} allListings={allListings} />
      </div>

      {showListingReport && <ListingReportModal listing={listing} onClose={() => setShowListingReport(false)} />}
    </main>
  );
}
